// frontend/src/context/NotificationContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import RealtimePoll from "../hooks/RealtimePoll";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { toast } from "react-toastify";
const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);

  // Load existing notifications once
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/notifications/updated-since`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
            params: { timestamp: "2000-01-01T00:00:00Z" },
          }
        );
        if (Array.isArray(res.data)) setNotifications(res.data);
      } catch (e) {
        console.warn("Initial notification load failed:", e.message);
      }
    })();
    // Start polling
    RealtimePoll.start("notifications", {});
    RealtimePoll.on("notifications", handleNew);
    return () => {
      RealtimePoll.off("notifications");
      RealtimePoll.stop("notifications");
    };
  }, [user?.id]);

  // Handle incoming notifications
  const handleNew = (items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    setNotifications((prev) => {
      const existingIds = new Set(prev.map((x) => x.id));
      const newItems = items.filter((x) => !existingIds.has(x.id));
      if (newItems.length === 0) return prev;

      newItems.forEach(n => {
        const data = n.data || {};
        let msg = data.message || n.type;
        if (n.type === 'project_created') {
          msg = `🆕 ${data.message || `Project ${data.project_name} created`}`;
        } else if (n.type === 'task_created') {
          msg = `🆕 ${data.message || `Task ${data.task_title} created`}`;
        } else if (n.type === 'project_assigned') {
          msg = `👥 ${data.message || `You were assigned to ${data.project_name}`}`;
        } else if (n.type === 'task_assigned') {
          msg = `👥 ${data.message || `You were assigned to task ${data.task_title}`}`;
        } else if (n.type === 'project_commented' || n.type === 'task_commented') {
          msg = `💬 ${data.actor}: ${(data.comment || data.message || "").slice(0, 100)}`;
        } else if (n.type === 'task_deleted') {
          msg = `🗑️ ${data.message || `${data.actor} deleted task "${data.task_title}"`}`;
        } else if (n.type === 'kpi_approval_pending') {
          msg = `📋 ${data.user_name} submitted ${data.kpis_count} KPI(s) for approval`;
        }
        toast.info(msg); // ✅ THIS WILL SHOW TOAST
      });

      return [...newItems, ...prev].slice(0, 200);
    });
  };

  // Mark all notifications as read
  const markAsRead = async (ids) => {
    if (ids.length === 0) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/notifications/mark-read`,
        { ids },
        { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } }
      );
      setNotifications(prev =>
        prev.map(n => (ids.includes(n.id) ? { ...n, is_read: true } : n))
      );
    } catch (e) {
      console.warn("Failed to mark as read:", e.message);
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;
    await markAsRead(unreadIds);
  };

  return (
    <NotificationContext.Provider value={{ notifications, markAsRead, markAllAsRead }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);